import React, { useEffect } from 'react';
import { Info, X } from 'lucide-react';
import type { DeepLinkShortCodeLookup } from '../../../models/MasterAdmin/deepLink';
import { DEEP_LINK_TARGET_APP_OPTIONS } from '../../../models/MasterAdmin/deepLink';
import CopyLinkButton from './CopyLinkButton';
import {
  dlModalHeaderClass,
  dlModalOverlayClass,
  dlTableCellClass,
  dlTableHeadClass,
  dlTableRowClass,
  dlTableWrapClass,
} from './deepLinkUi';

type Props = {
  open: boolean;
  result: DeepLinkShortCodeLookup | null;
  onClose: () => void;
};

type Row = { label: string; value: React.ReactNode };

function fmtDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function prefixLabel(prefix?: string | null) {
  if (prefix === 'p') return 'Plus (/p)';
  if (prefix === 'l') return 'Legacy (/l)';
  if (prefix === 'u') return 'User (/u)';
  return prefix || '—';
}

function kindLabel(kind?: string) {
  if (kind === 'intent') return 'Personalized short-intent';
  if (kind === 'campaign') return 'Shared campaign';
  if (kind === 'expired_intent') return 'Expired short-intent';
  if (kind === 'open') return 'Long /open link';
  if (kind === 'not_found') return 'Not found';
  return kind || '—';
}

function appLabel(appKey?: string | null) {
  return (
    DEEP_LINK_TARGET_APP_OPTIONS.find((option) => option.value === appKey)?.label ||
    appKey ||
    '—'
  );
}

function KeyValueTable({ rows }: { rows: Row[] }) {
  return (
    <div className={dlTableWrapClass}>
      <table className="min-w-full text-sm">
        <thead className={dlTableHeadClass}>
          <tr>
            <th className="w-44 px-4 py-2">Field</th>
            <th className="px-4 py-2">Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className={dlTableRowClass}>
              <td className={`${dlTableCellClass} whitespace-nowrap text-gray-500 dark:text-gray-400`}>
                {row.label}
              </td>
              <td className={`${dlTableCellClass} break-all`}>{row.value ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-xs">{children || '—'}</span>;
}

function LinkOrDash({ href }: { href?: string | null }) {
  if (!href) return <span>—</span>;
  return (
    <span className="inline-flex max-w-full items-start gap-1">
      <a href={href} target="_blank" rel="noreferrer" className="break-all text-[#128C7E] underline">
        {href}
      </a>
      <CopyLinkButton text={href} label="Copy" />
    </span>
  );
}

export function DeepLinkCheckerHelpTooltip() {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        aria-label="How short-code check works"
      >
        <Info className="h-4 w-4" />
      </button>
      <span className="pointer-events-none absolute right-0 top-full z-30 mt-2 hidden w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-3 text-left text-xs leading-5 text-gray-700 shadow-lg group-hover:block group-focus-within:block dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
        Paste a User <span className="font-mono">/u/</span> or Plus <span className="font-mono">/p/</span>{' '}
        short link, or just the code. Check inspects the intent, campaign, navigation, and params
        without recording a click.
        <br />
        Personalized WhatsApp codes do not appear in the campaign list. Params live on the intent
        row, not the parent campaign.
      </span>
    </span>
  );
}

export function DeepLinkInspectSummaryTooltip({ result }: { result: DeepLinkShortCodeLookup }) {
  const code = result.parsed?.shortCode || result.shortCode || '—';
  const intended = result.intended;
  const appBits = [
    intended?.appRoute,
    intended?.appArgs?.speciality ? `speciality ${intended.appArgs.speciality}` : null,
    intended?.appArgs?.consultationType || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        aria-label="Link inspect summary"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      <span className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-[min(28rem,calc(100vw-2rem))] space-y-1.5 rounded-xl border border-gray-200 bg-white p-3 text-left text-xs leading-5 text-gray-700 shadow-lg group-hover:block group-focus-within:block dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
        <p className="font-semibold">
          {result.valid ? 'Valid' : 'Not live'} · {result.kind} · {code}
        </p>
        <p>{result.message}</p>
        {intended?.webPath ? (
          <p>
            Intended web: <span className="font-mono">{intended.webPath}</span>
          </p>
        ) : null}
        {intended?.webUrl ? <p>Web URL: {intended.webUrl}</p> : null}
        {appBits ? <p>App route: {appBits}</p> : null}
        {(result.diagnose?.issues || []).map((issue) => (
          <p key={issue}>{issue}</p>
        ))}
        {(result.diagnose?.notes || []).map((note) => (
          <p key={note}>{note}</p>
        ))}
        {result.campaign ? (
          <p>
            Parent campaign: {result.campaign.name}
            {result.campaign.shortCode ? ` (${result.campaign.shortCode})` : ''}
            {result.campaign.routeSlug ? ` · ${result.campaign.routeSlug}` : ''}
            {result.campaign.status ? ` · ${result.campaign.status}` : ''}
          </p>
        ) : null}
        {result.intent ? (
          <p>
            Source {result.intent.source || '—'} · redeemed {result.intent.redeemedCount ?? 0}
            {result.intent.expiresAt ? ` · expires ${fmtDate(result.intent.expiresAt)}` : ''}
            {result.intent.createdAt ? ` · created ${fmtDate(result.intent.createdAt)}` : ''}
          </p>
        ) : null}
      </span>
    </span>
  );
}

const DeepLinkCodeInspectModal: React.FC<Props> = ({ open, result, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !result) return null;

  const code = result.parsed?.shortCode || result.shortCode || '—';
  const prefix = result.prefix || result.parsed?.prefix || null;
  const liveUrl =
    result.liveUrl ||
    (code && code !== '—' && prefix ? `https://vedika.health/${prefix}/${code}` : null);
  const publicCheckUrl =
    result.publicCheckUrl || (liveUrl ? `${liveUrl}?format=json` : null);
  const params = result.resolvedRouteParams || result.routeParams || {};
  const paramRows = Object.entries(params);
  const intended = result.intended;
  const campaign = result.campaign;
  const intent = result.intent;
  const diagnose = result.diagnose;

  return (
    <div
      className={dlModalOverlayClass}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-[96vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 lg:max-w-6xl">
        <div className={dlModalHeaderClass}>
          <div className="min-w-0 pr-4">
            <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-white">
              Link inspect · {code}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {result.valid ? 'Valid' : 'Not live'} · {kindLabel(result.kind)} · {prefixLabel(prefix)}
              {result.inspectHint ? ` · ${result.inspectHint}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-gray-500 dark:text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-4 sm:p-6 lg:grid-cols-2">
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Link review</h3>
            <KeyValueTable
              rows={[
                {
                  label: 'Status',
                  value: result.valid ? (
                    <span className="font-medium text-emerald-700 dark:text-emerald-300">Valid</span>
                  ) : (
                    <span className="font-medium text-amber-700 dark:text-amber-300">Not live</span>
                  ),
                },
                { label: 'Kind', value: kindLabel(result.kind) },
                { label: 'Short code', value: <Mono>{code}</Mono> },
                { label: 'Prefix', value: prefixLabel(prefix) },
                { label: 'App', value: appLabel(result.appKey || campaign?.targetAppKey) },
                { label: 'Message', value: result.message || '—' },
                { label: 'Live URL', value: <LinkOrDash href={liveUrl} /> },
                { label: 'Public JSON', value: <LinkOrDash href={publicCheckUrl} /> },
              ]}
            />
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Navigation</h3>
            <KeyValueTable
              rows={[
                { label: 'Route slug', value: <Mono>{intended?.routeSlug || result.route?.slug}</Mono> },
                { label: 'Intended web path', value: <Mono>{intended?.webPath}</Mono> },
                { label: 'Web URL', value: <LinkOrDash href={intended?.webUrl} /> },
                { label: 'App route', value: <Mono>{intended?.appRoute || result.route?.appRoute}</Mono> },
                { label: 'Speciality', value: intended?.appArgs?.speciality || '—' },
                { label: 'Speciality ID', value: <Mono>{intended?.appArgs?.specialityId}</Mono> },
                { label: 'Consultation type', value: intended?.appArgs?.consultationType || '—' },
                {
                  label: 'Login required',
                  value: intended?.loginRequired ? 'Yes' : 'No',
                },
              ]}
            />
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Params</h3>
            <div className={dlTableWrapClass}>
              <table className="min-w-full text-sm">
                <thead className={dlTableHeadClass}>
                  <tr>
                    <th className="w-44 px-4 py-2">Param</th>
                    <th className="px-4 py-2">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {paramRows.length ? (
                    paramRows.map(([key, value]) => (
                      <tr key={key} className={dlTableRowClass}>
                        <td className={`${dlTableCellClass} font-mono text-xs`}>{key}</td>
                        <td className={`${dlTableCellClass} break-all font-mono text-xs`}>
                          {String(value)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className={`${dlTableCellClass} text-gray-500`}>
                        No route params on this code.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Campaign</h3>
            <KeyValueTable
              rows={
                campaign
                  ? [
                      { label: 'Name', value: campaign.name },
                      { label: 'Campaign code', value: <Mono>{campaign.shortCode}</Mono> },
                      { label: 'Status', value: campaign.status || '—' },
                      { label: 'Route slug', value: <Mono>{campaign.routeSlug}</Mono> },
                      { label: 'App', value: appLabel(campaign.targetAppKey) },
                      { label: 'Campaign URL', value: <LinkOrDash href={campaign.shortUrl} /> },
                    ]
                  : [{ label: 'Campaign', value: 'No parent campaign' }]
              }
            />
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Short-intent</h3>
            <KeyValueTable
              rows={
                intent
                  ? [
                      { label: 'Intent code', value: <Mono>{intent.shortCode}</Mono> },
                      { label: 'Source', value: intent.source || '—' },
                      { label: 'Redeemed', value: String(intent.redeemedCount ?? 0) },
                      { label: 'Created', value: fmtDate(intent.createdAt) },
                      { label: 'Expires', value: fmtDate(intent.expiresAt) },
                      { label: 'App', value: appLabel(intent.targetAppKey) },
                    ]
                  : [
                      {
                        label: 'Intent',
                        value:
                          result.kind === 'campaign'
                            ? 'Shared campaign code — no personalized intent row'
                            : 'No short-intent row',
                      },
                    ]
              }
            />
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Diagnose</h3>
            <div className={dlTableWrapClass}>
              <table className="min-w-full text-sm">
                <thead className={dlTableHeadClass}>
                  <tr>
                    <th className="w-28 px-4 py-2">Type</th>
                    <th className="px-4 py-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(diagnose?.issues || []).map((issue) => (
                    <tr key={`issue-${issue}`} className={dlTableRowClass}>
                      <td className={`${dlTableCellClass} text-amber-700 dark:text-amber-300`}>Issue</td>
                      <td className={dlTableCellClass}>{issue}</td>
                    </tr>
                  ))}
                  {(diagnose?.notes || []).map((note) => (
                    <tr key={`note-${note}`} className={dlTableRowClass}>
                      <td className={`${dlTableCellClass} text-gray-500 dark:text-gray-400`}>Note</td>
                      <td className={dlTableCellClass}>{note}</td>
                    </tr>
                  ))}
                  {!diagnose?.issues?.length && !diagnose?.notes?.length ? (
                    <tr>
                      <td colSpan={2} className={`${dlTableCellClass} text-gray-500`}>
                        No extra diagnose notes.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeepLinkCodeInspectModal;
